/**
 * research system — v0M9.4 (2026-07-24).
 *
 * Advances each faction's `activeResearch.progressDays` by DAYS_PER_TICK
 * each tick. On completion (progressDays >= knowledge.researchDays):
 *   - Knowledge name appended to `faction.researchedKnowledge`.
 *   - `activeResearch` cleared.
 *
 * Learning cost was deducted up-front at `start_research` command time
 * (see `apply-commands.ts`), so a completed research does not deduct
 * anything here. Similarly a canceled research (post-v1 feature) would
 * not refund learning.
 *
 * One-active-at-a-time is enforced at command-issue time; this system
 * only advances progress. If a fixture ever produces a state with
 * `activeResearch` set but the knowledge name is unknown to the loader
 * (bad save / bad test fixture), the system defensively clears
 * activeResearch and moves on rather than throwing.
 *
 * Ordering (systems.ts): runs after `economy` (so learning produced
 * this tick is banked, and any completion this tick is visible to
 * subsequent tick systems). Placement is between `economy` and
 * `supply`; MP-safe because it only touches faction-scoped state.
 *
 * Determinism: iterates factions via `sortedIds` per MECHANICS.md §1.1
 * rule 3. No RNG consumption.
 */

import type { Command } from './commands';
import type { GameState } from './state';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import { TICKS_PER_GAME_DAY, sortedIds } from './state';
import { loadKnowledge } from '../data/knowledge';

const KNOWLEDGE = loadKnowledge();
const DAYS_PER_TICK = 1 / TICKS_PER_GAME_DAY;

export function researchSystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  _context: SimContext,
): GameState {
  for (const factionId of sortedIds(state.factions)) {
    const faction = state.factions.get(factionId);
    if (!faction) continue;

    // Step 1 — advance any active research; on completion clear
    // activeResearch (queue promotion happens in Step 2).
    const active = faction.activeResearch;
    if (active) {
      const spec = KNOWLEDGE[active.knowledgeName];
      if (!spec) {
        // Unknown knowledge name — defensive cleanup (bad save / stale fixture).
        faction.activeResearch = null;
      } else {
        active.progressDays += DAYS_PER_TICK;
        if (active.progressDays >= spec.researchDays) {
          // Complete. Append to researched list (materializing the array if
          // the faction is a legacy fixture without the field).
          if (!faction.researchedKnowledge) faction.researchedKnowledge = [];
          // Guard against a race where the same knowledge somehow completed
          // twice — cheap and belt-and-suspenders per the SG determinism
          // discipline.
          if (!faction.researchedKnowledge.includes(active.knowledgeName)) {
            faction.researchedKnowledge.push(active.knowledgeName);
          }
          faction.activeResearch = null;
        }
      }
    }

    // Step 2 — v0M6.3 (2026-07-26) queue promotion. If activeResearch
    // is now null and the queue has items, try to promote the head
    // item. Learning is deducted at promotion (session-33 elicited
    // policy) — insufficient Learning pauses the queue in place: the
    // item stays at head and re-checks next tick when Learning may
    // have accrued from the economy pass earlier this tick. Also
    // handles the "cancel-active leaves queue behind" case naturally.
    if ((faction.activeResearch ?? null) === null && faction.researchQueue && faction.researchQueue.length > 0) {
      const nextName = faction.researchQueue[0]!;
      const spec = KNOWLEDGE[nextName];
      if (!spec) {
        // Unknown knowledge in queue — drop it and try again next tick.
        faction.researchQueue.shift();
      } else if (faction.resources.learning >= spec.learningCost) {
        // Also validate prereq still holds — belt-and-suspenders since
        // the enqueue check accepted queued/active prereqs, and the
        // active-prereq case may have been cancelled in between.
        const prereq = spec.prerequisite;
        const already = faction.researchedKnowledge ?? [];
        const prereqOk = prereq === '' || already.includes(prereq);
        if (prereqOk) {
          faction.resources.learning -= spec.learningCost;
          faction.researchQueue.shift();
          faction.activeResearch = { knowledgeName: nextName, progressDays: 0 };
        }
        // If prereq is broken (its parent got cancelled mid-queue),
        // leave the item at head; the player has to cancel it or
        // enqueue the parent to unblock. Simplest sane behaviour.
      }
      // else: insufficient Learning — queue pauses in place.
    }
  }
  return state;
}
